<script lang="ts">
	import { untrack } from 'svelte';
	import type { CalendarEvent, CalendarView, KeyItem } from '@/types/calendar';
	import type { Snippet } from 'svelte';
	import CalendarHeader from './CalendarHeader.svelte';
	import MonthView from './views/MonthView.svelte';
	import WeekView from './views/WeekView.svelte';
	import DayView from './views/DayView.svelte';
	import { getToday, parseDateString, getDateRange, formatDateToString } from './calendar.utils';
	import { Loader2 } from 'lucide-svelte';

	interface Props {
		events: CalendarEvent[];
		defaultView?: CalendarView;
		defaultDate?: string;
		isLoading?: boolean;
		showPopover?: boolean;
		onEventClick?: (event: CalendarEvent) => void;
		onDateRangeChange?: (startDate: string, endDate: string) => void;
		onViewChange?: (view: CalendarView) => void;
		filters?: Snippet;
		legendItems?: KeyItem[];
		activeFilterKeys?: string[];
		onFilterToggle?: (key: string) => void;
		onFilterReset?: () => void;
		stickyTopOffset?: number;
	}

	let {
		events,
		defaultView = 'month',
		defaultDate,
		isLoading = false,
		showPopover = true,
		onEventClick,
		onDateRangeChange,
		onViewChange,
		filters,
		legendItems,
		activeFilterKeys,
		onFilterToggle,
		onFilterReset,
		stickyTopOffset = 0
	}: Props = $props();

	// Initialize state with defaults on mount
	let currentView = $state<CalendarView>(untrack(() => defaultView));
	let currentDate = $state(untrack(() => (defaultDate ? parseDateString(defaultDate) : getToday())));

	// Track view and date changes to trigger date range callback
	$effect(() => {
		if (onDateRangeChange) {
			const { start, end } = getDateRange(currentView, currentDate);
			onDateRangeChange(formatDateToString(start), formatDateToString(end));
		}
	});

	// Measure CalendarHeader height so views can sticky below it
	let calendarHeaderHeight = $state(0);

	// Navigation handlers
	function handleDateChange(date: typeof currentDate) {
		currentDate = date;
	}

	function handleViewChange(view: CalendarView) {
		currentView = view;
		onViewChange?.(view);
	}
</script>

<div
	class="relative flex h-full flex-col"
	style="--view-sticky-top: calc(var(--header-height, 0px) + {stickyTopOffset + calendarHeaderHeight}px); --calendar-header-top: calc(var(--header-height, 0px) + {stickyTopOffset}px)"
>
	<!-- Loading Overlay -->
	{#if isLoading}
		<div
			class="absolute inset-0 z-50 flex items-center justify-center bg-background/50 backdrop-blur-sm"
		>
			<Loader2 class="h-8 w-8 animate-spin text-primary" />
		</div>
	{/if}

	<!-- Header -->
	<CalendarHeader
		bind:height={calendarHeaderHeight}
		{currentDate}
		{currentView}
		onDateChange={handleDateChange}
		onViewChange={handleViewChange}
		{filters}
		{legendItems}
		{activeFilterKeys}
		{onFilterToggle}
		{onFilterReset}
	/>

	<!-- Calendar View -->
	<div class="flex-1">
		{#if currentView === 'month'}
			<MonthView {events} {currentDate} {showPopover} {onEventClick} />
		{:else if currentView === 'week'}
			<WeekView {events} {currentDate} {showPopover} {onEventClick} />
		{:else}
			<DayView {events} {currentDate} {showPopover} {onEventClick} />
		{/if}
	</div>
</div>
